import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import Accueil from './pages/Accueil/Accueil';
import APropos from './pages/APropos/APropos';
import Catalogue from './pages/Catalogue/Catalogue';
import Contact from './pages/Contact/Contact';
import Agenda from './pages/Agenda/Agenda';
import Galerie from './pages/Galerie/Galerie';
import MentionsLegales from './pages/MentionsLegales/MentionsLegales';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite/PolitiqueConfidentialite';
import CGV from './pages/CGV/CGV';
import FavoritesSidebar from './components/FavoritesSidebar/FavoritesSidebar';
import CartSidebar from './components/CartSidebar/CartSidebar';
import StickyButtons from './components/StickyButtons/StickyButtons';
import ScrollToTop from './components/ScrollToTop';
import ProductDetail from './pages/ProductDetail/ProductDetail';
import SoldItemDetail from './pages/SoldItemDetail/SoldItemDetail'; // Importer la page de détail pour la galerie
import AuthPage from './pages/Auth/AuthPage';
import AccountPage from './pages/Account/AccountPage';
import Dashboard from './pages/Dashboard/Dashboard';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { CartProvider } from './context/CartContext';
import { ChatProvider } from './context/ChatContext';
import chatNotificationService from './services/chatNotificationService';
import { pushNotificationService } from './services/pushService';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './App.css';

const MainApp = ({ deferredPrompt, showInstallButton, onInstallClick }) => {
  const { user, isLoggedIn } = useAuth();
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    // Initialiser les notifications globales si l'utilisateur est connecté
    if (isLoggedIn && user) {
      chatNotificationService.initializeChatNotifications(user);
      pushNotificationService.subscribeUser();
    }

    return () => {
      chatNotificationService.stopChatNotifications();
    };
  }, [isLoggedIn, user]);

  return (
    <>
      <Navbar />
      <ToastContainer />
      <FavoritesSidebar />
      <CartSidebar />
      {!isAdminPage && <StickyButtons />}
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Accueil />} />
          <Route path="/a-propos" element={<APropos />} />
          <Route path="/catalogue" element={<Catalogue />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/galerie" element={<Galerie />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/catalogue/produit/:slug" element={<ProductDetail />} />
          <Route path="/galerie/vendu/:slug" element={<SoldItemDetail />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route 
            path="/mon-compte" 
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/*" 
            element={
              <ProtectedRoute requiredRole="admin">
                <Dashboard />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </main>
      {!isAdminPage && <Footer showInstallButton={showInstallButton} onInstallClick={onInstallClick} />}
    </>
  );
}

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`Installation : ${outcome}`);
      if (outcome === 'accepted') {
        setShowInstallButton(false);
        setDeferredPrompt(null);
      }
    }
  };

  return (
    <AuthProvider>
      <ChatProvider>
        <FavoritesProvider>
          <CartProvider>
            <DndProvider backend={HTML5Backend}>
              <Router>
                <ScrollToTop />
                <div className="app-container">
                  <MainApp 
                    deferredPrompt={deferredPrompt}
                    showInstallButton={showInstallButton}
                    onInstallClick={handleInstallClick}
                  />
                </div>
              </Router>
            </DndProvider>
          </CartProvider>
        </FavoritesProvider>
      </ChatProvider>
    </AuthProvider>
  );
}

export default App;